// BALTIVEX brand mark — SVG re-implementations of the hexagonal logo emblem.
// These components are the foundation of the recurring visual identity used
// throughout the website. They are scale-/color-friendly and have no white
// background, so they blend with any surface (white, light grey or navy).
//
// • HexMark      — clean single-stroke hex outline (currentColor). Use anywhere a
//                  subtle brand motif is needed (icons, dividers, decoration).
// • BrandHex     — full-color hex emblem (navy / grey / green panels) mirroring
//                  the official BALTIVEX logo. Use where the brand mark should
//                  be visible at moderate size.
// • HexWatermark — oversized, ultra-low-opacity hex background watermark for
//                  major sections. Absolute-positioned, decorative only.

import React from "react";

const NAVY = "#0E2A47";
const SILVER = "#C9CFD7";
const TEAL = "#2A6B6A";
const GREEN = "#1FA86B";

// Regular hexagon (pointy-top) outline path inside a 100×100 viewBox.
// 6 vertices: 50,4 — 92,28 — 92,72 — 50,96 — 8,72 — 8,28
const HEX_OUTER = "M50,4 L92,28 L92,72 L50,96 L8,72 L8,28 Z";
// Inner hex (40 % smaller), same orientation — used to form the hex "ring".
const HEX_INNER = "M50,28 L72,40 L72,60 L50,72 L28,60 L28,40 Z";

/** Plain hex outline. Inherits color via currentColor. */
export function HexMark({ size = 28, strokeWidth = 2, filled = false, className = "" }) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
      role="img"
    >
      <path
        d={HEX_OUTER}
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinejoin="miter"
      />
    </svg>
  );
}

/** Full-color BALTIVEX brand emblem (4-segment hex ring). */
export function BrandHex({ size = 64, className = "", title = "BALTIVEX" }) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <defs>
        <clipPath id="hex-ring">
          {/* outer hex minus inner hex = ring */}
          <path d={`${HEX_OUTER} ${HEX_INNER}`} fillRule="evenodd" />
        </clipPath>
      </defs>
      <g clipPath="url(#hex-ring)">
        {/* Top-left navy panel */}
        <polygon points="8,28 50,4 50,50 8,50" fill={NAVY} />
        {/* Top-right silver panel */}
        <polygon points="50,4 92,28 92,50 50,50" fill={SILVER} />
        {/* Bottom-left teal panel */}
        <polygon points="8,50 50,50 50,96 8,72" fill={TEAL} />
        {/* Bottom-right green panel */}
        <polygon points="50,50 92,50 92,72 50,96" fill={GREEN} />
      </g>
      {/* Subtle inner edge to keep the ring legible at small sizes */}
      <path d={HEX_INNER} fill="none" stroke="rgba(255,255,255,0.0)" strokeWidth="0" />
    </svg>
  );
}

/**
 * HexWatermark — large, ultra-low-opacity hex used as a section background
 * decoration. Position with absolute classes via wrapper.
 */
export function HexWatermark({
  className = "",
  opacity = 0.05,
  size = 720,
  strokeWidth = 1.2,
  filled = false,
  rotate = 0,
}) {
  return (
    <div
      className={`pointer-events-none select-none absolute ${className}`}
      style={{ opacity, transform: `rotate(${rotate}deg)`, width: size, height: size }}
      aria-hidden="true"
    >
      <HexMark size={size} strokeWidth={strokeWidth} filled={filled} />
    </div>
  );
}
